// src/componentes/olt/ModalPasos.jsx — el modal «por pasos» de la pantalla de ONUs
//
// El diseño que se eligió el 2026-09-13 en el banco de pruebas (tratamiento
// C). Lo usan los diez modales que hablan con la OLT: cada uno pone su
// formulario y sus fases; esto pone todo lo demás.
//
// CÓMO SE CUENTA LO QUE PASA, SIN INVENTAR NADA
// ------------------------------------------------------------------
// Cada operación es una petición HTTP que devuelve TODOS los comandos al
// final, con lo que contestó el equipo a cada uno. Así que:
//
//   · mientras se espera, se dice que se espera, con el reloj corriendo —
//     y ninguna fase se marca, porque no se sabe por dónde va;
//   · cuando llega la respuesta, se REPRODUCE: cada comando real se marca
//     uno detrás de otro, con su respuesta y sus milisegundos.
//
// Antes estos modales pintaban pasos con `setTimeout` a 450, 950, 1400 ms
// que no sabían nada de la OLT. Aquí cada marca es una línea que el equipo
// contestó de verdad. Donde sí se puede ir en vivo —dar de alta una ONU
// son cuatro peticiones seguidas— el modal que llama pasa el estado de
// cada fase y esto lo respeta.
//
// NADA SE REPINTA ENTERO. Las filas llevan claves estables y lo que cambia
// son las clases: los colores y el despliegue los hace el CSS con
// transiciones. Rehacer el HTML en cada paso era lo que parpadeaba.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Iconos } from "../base";
import "./ModalPasos.css";

/* Las fórmulas con las que esta OLT dice que NO. */
const RECHAZO = /^\s*(Failure|Error)\s*:|%\s*(Unknown|Wrong|Incomplete|Too many|Invalid)/mi;

/* Comandos cuyo «error» no significa nada. La sesión ya entra en `config`
   al conectar, así que `enable` contesta «Unknown command» y `config»
   «Incomplete command» siempre. Pintarlos en rojo sería asustar por nada. */
const INOFENSIVOS = /^(enable|config)$/;

/* Un `undo` de algo que no estaba. Configurar la WAN limpia primero lo que
   hubiera —hace falta al reconfigurar una ONT que ya tenía WAN—, y en una
   ONT recién dada de alta no hay nada: la OLT contesta «does not exist» o
   «does not been bound». No es un fallo, y en ámbar se leía como uno. */
const NADA_QUE_LIMPIAR = /does not exist|not been bound|is not bound/i;
const esLimpieza = (f) => /^undo\s/.test(f.cmd) && NADA_QUE_LIMPIAR.test(f.out);

/* Lo que tarda en marcarse cada comando al reproducir. Lo bastante lento
   para seguirlo con la vista, lo bastante rápido para no hacer esperar:
   una WAN de doce comandos se cuenta en poco más de un segundo. */
const PASO_MS = 110;

/* ---------- utilidades para los modales ---------- */

/** Lee el JSON de una respuesta, y si no lo es, dice qué llegó. */
export async function leerRespuesta(res) {
  const tipo = res.headers.get("content-type") || "";
  if (!tipo.includes("application/json")) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} — el servidor no devolvió JSON: ${txt.slice(0, 160)}`);
  }
  return res.json().catch(() => ({}));
}

/** Dónde vive una ONU, como la guarda la base. */
export function ubicacionDe(onu) {
  const fsp = String(onu?.fsp || onu?.port || "").trim();
  const partes = fsp.split("/");
  return {
    fsp: fsp || "—",
    frameSlot: partes.length >= 2 ? `${partes[0]}/${partes[1]}` : "?/?",
    pon: partes.length === 3 ? partes[2] : "?",
    ont: onu?.ont_id ?? onu?.onu_id ?? "?",
  };
}

/** La ficha de siempre: a quién se le va a hacer. */
export function fichaOnu(onu) {
  const u = ubicacionDe(onu);
  const serie = String(onu?.serial || onu?.sn || "");
  return [
    ["Cliente", onu?.cliente_id ?? "—"],
    ["Puerto", u.fsp, true],
    ["ONT", u.ont, true],
    ["Serie", serie ? `…${serie.slice(-8)}` : "—", true],
  ];
}

/* ---------- el reparto de la salida en fases ---------- */

const normalizar = (o) => ({
  cmd: String(o?.cmd ?? o?.command ?? "").trim(),
  out: String(o?.output ?? o?.out ?? o?.salida ?? "").trim(),
  ms: o?.ms ?? null,
});

/* Cada comando cae en la primera fase —desde la actual en adelante— cuyo
   patrón lo reconoce. Nunca se retrocede: así un `quit` o un
   `interface gpon` que se repiten caen en la fase que les toca por orden.
   Y lo que no reconoce ninguna se queda en la fase en curso: no se pierde
   una sola línea de lo que contestó el equipo. */
export function repartir(fases, salida) {
  if (fases.some((f) => Array.isArray(f.salida))) {
    return fases.map((f) => (f.salida || []).map(normalizar));
  }
  const filas = fases.map(() => []);
  let actual = 0;
  (salida || []).map(normalizar).forEach((o) => {
    for (let i = actual; i < fases.length; i++) {
      if (fases[i].coincide && fases[i].coincide.test(o.cmd)) { actual = i; break; }
    }
    if (filas[actual]) filas[actual].push(o);
  });
  return filas;
}

/* ---------- el componente ---------- */

export default function ModalPasos({
  abierto,
  titulo,
  icono: Icono,
  ficha = [],
  aviso,
  peligro = false,
  mantener,
  arriba,
  children,
  fases = [],
  estado = "reposo",
  salida,
  error,
  mensaje,
  tono = "bien",
  resultado,
  verbo,
  listo = true,
  consulta = false,
  textoEspera = "Esperando a la OLT",
  onEjecutar,
  onCerrar,
}) {
  const conGesto = (mantener ?? peligro) && !consulta;
  const [reveladas, setReveladas] = useState(0);
  const [segundos, setSegundos] = useState(0);
  const [gesto, setGesto] = useState(false);
  const relleno = useRef(null);
  const raf = useRef(0);
  const desde = useRef(0);

  const porFase = useMemo(() => repartir(fases, salida), [fases, salida]);
  const planas = useMemo(
    () => porFase.flatMap((filas, fi) => filas.map((f, ri) => ({ ...f, fi, ri }))),
    [porFase],
  );
  const total = planas.length;

  // Una ejecución nueva empieza desde cero. También al pasar a
  // «corriendo»: una consulta que se repite va de «fin» a «corriendo» sin
  // pasar por «reposo», y sin esto la segunda lectura saldría ya marcada.
  useEffect(() => {
    if (estado === "reposo" || estado === "corriendo") { setReveladas(0); setSegundos(0); }
  }, [estado]);

  // El reloj corre de verdad mientras se espera.
  useEffect(() => {
    if (estado !== "corriendo") return undefined;
    const t0 = performance.now();
    const id = setInterval(() => setSegundos((performance.now() - t0) / 1000), 100);
    return () => clearInterval(id);
  }, [estado]);

  // La reproducción: una línea real cada PASO_MS.
  useEffect(() => {
    if (estado === "reposo" || reveladas >= total) return undefined;
    const t = setTimeout(() => setReveladas((r) => r + 1), PASO_MS);
    return () => clearTimeout(t);
  }, [estado, reveladas, total]);

  // Escape cierra, salvo mientras se está mandando.
  useEffect(() => {
    if (!abierto) return undefined;
    const alPulsar = (e) => { if (e.key === "Escape" && estado !== "corriendo") onCerrar?.(); };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, [abierto, estado, onCerrar]);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const repasado = reveladas >= total;
  const terminado = estado === "fin" && repasado;

  /* Cuál es EL fallo. Si la operación salió mal, el último rechazo del
     equipo es el que la tumbó; los anteriores —un `undo` de algo que no
     existía— son protestas que no importaron, y se marcan en ámbar. */
  const indiceFallo = useMemo(() => {
    if (!(estado === "fin" && error)) return -1;
    for (let i = planas.length - 1; i >= 0; i--) {
      if (RECHAZO.test(planas[i].out) && !INOFENSIVOS.test(planas[i].cmd)
          && !esLimpieza(planas[i])) return i;
    }
    return -1;
  }, [estado, error, planas]);

  const tipoFila = (i) => {
    if (i >= reveladas) return i === reveladas && estado !== "reposo" ? "activo" : "pendiente";
    const f = planas[i];
    if (!RECHAZO.test(f.out)) return "hecho";
    if (INOFENSIVOS.test(f.cmd) || esLimpieza(f)) return "ignorado";
    return i === indiceFallo ? "mal" : "aviso";
  };

  const estadoFase = (fi) => {
    if (fases[fi].estado) return fases[fi].estado;
    if (estado === "reposo") return "pendiente";
    const indices = planas.map((p, i) => (p.fi === fi ? i : -1)).filter((i) => i >= 0);
    if (indices.length) {
      if (indices.some((i) => i < reveladas && tipoFila(i) === "mal")) return "mal";
      if (indices.every((i) => i < reveladas)) return "hecho";
      if (indices.some((i) => i <= reveladas) && estado !== "reposo") return "activo";
      return "pendiente";
    }
    if (!terminado) return "pendiente";
    if (!error) return "hecho";
    const faseFallo = indiceFallo >= 0 ? planas[indiceFallo].fi : -1;
    return faseFallo >= 0 && fi < faseFallo ? "hecho" : "cancelado";
  };

  /* ---------- mantener pulsado ---------- */

  const soltar = () => {
    cancelAnimationFrame(raf.current);
    if (relleno.current) relleno.current.style.transform = "scaleX(0)";
    setGesto(false);
  };
  const avanzar = () => {
    const p = Math.min(1, (performance.now() - desde.current) / 1000);
    if (relleno.current) relleno.current.style.transform = `scaleX(${p})`;
    if (p >= 1) { soltar(); onEjecutar?.(); return; }
    raf.current = requestAnimationFrame(avanzar);
  };
  const manejadores = conGesto
    ? {
      onPointerDown: (e) => {
        if (estado !== "reposo" || !listo) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        desde.current = performance.now();
        setGesto(true);
        avanzar();
      },
      onPointerUp: soltar,
      onPointerCancel: soltar,
      // Con teclado no se puede «mantener»: llegar hasta aquí con el
      // tabulador ya es la intención.
      onKeyDown: (e) => {
        if ((e.key === "Enter" || e.key === " ") && estado === "reposo" && listo) {
          e.preventDefault();
          onEjecutar?.();
        }
      },
    }
    : { onClick: () => onEjecutar?.() };

  if (!abierto) return null;

  const estadoModal = estado === "fin" ? (terminado ? (error ? "fin-mal" : "fin-ok") : "repaso") : estado;

  /* ---------- las fases ---------- */

  const listaFases = (
    <ol className="mp-pasos">
      {fases.map((f, fi) => {
        const ef = estadoFase(fi);
        const reales = porFase[fi] || [];
        const base = planas.findIndex((p) => p.fi === fi);
        return (
          <li key={`fase-${fi}`} className={`mp-paso mp-paso--${ef}`}>
            <span className="mp-num">
              {ef === "hecho" ? <Iconos.confirmar size={11} strokeWidth={3.4} />
                : ef === "mal" ? <Iconos.cerrar size={11} strokeWidth={3.4} />
                  : fi + 1}
            </span>
            <div className="mp-txt">
              <b>{f.rotulo}</b>
              {f.dato && <small className="mp-dato">{f.dato}</small>}

              {reales.length > 0 ? reales.map((r, ri) => {
                const tipo = tipoFila(base + ri);
                return (
                  <div key={`r-${fi}-${ri}`} className={`mp-cmd mp-cmd--${tipo}`}>
                    <div className="mp-linea">
                      <code>{r.cmd || "(enter)"}</code>
                      {tipo === "ignorado" && esLimpieza(r) && (
                        <small className="mp-nota">no había nada que limpiar</small>
                      )}
                      {r.ms != null && <em>{r.ms} ms</em>}
                    </div>
                    <div className="mp-resp"><div><pre>{r.out}</pre></div></div>
                  </div>
                );
              }) : f.app ? (
                <div className={`mp-cmd mp-cmd--app mp-cmd--${ef === "hecho" ? "hecho" : ef === "cancelado" ? "cancelado" : "pendiente"}`}>
                  <div className="mp-linea"><Iconos.documento size={12} strokeWidth={2} /><code>{f.detalle}</code></div>
                </div>
              ) : (f.cmds || []).map((c, ci) => (
                <div key={`p-${fi}-${ci}`}
                     className={`mp-cmd mp-cmd--${ef === "cancelado" ? "cancelado" : ef === "hecho" ? "sinsalida" : "pendiente"}`}>
                  <div className="mp-linea"><code>{c}</code></div>
                </div>
              ))}
            </div>
          </li>
        );
      })}
    </ol>
  );

  const remate = terminado && (
    <>
      {!error && resultado && <div className="mp-resultado">{resultado}</div>}
      <p className={`mp-remate mp-remate--${error ? "mal" : tono}`}>
        {error || tono === "mal" ? <Iconos.error size={16} strokeWidth={2} />
          : tono === "aviso" ? <Iconos.aviso size={16} strokeWidth={2} />
          : <Iconos.confirmar size={16} strokeWidth={2.6} />}
        <span>{error || mensaje || "Hecho."}</span>
      </p>
    </>
  );

  return createPortal(
    <div className="mp-velo" onMouseDown={(e) => { if (e.target === e.currentTarget && estado !== "corriendo") onCerrar?.(); }}>
      <div className="mp-caja" role="dialog" aria-modal="true" aria-label={titulo} data-estado-modal={estadoModal}>
        <div className="mp-cab">
          <h3>{Icono && <Icono size={18} strokeWidth={2} />}<span>{titulo}</span></h3>
          <button type="button" className="mp-x" onClick={onCerrar} disabled={estado === "corriendo"} aria-label="Cerrar">
            <Iconos.cerrar size={16} strokeWidth={2} />
          </button>
        </div>

        {ficha.length > 0 && (
          <div className="mp-ficha">
            {ficha.map(([k, v, mono]) => (
              <div key={k}><em>{k}</em><b className={mono ? "mono" : undefined}>{v}</b></div>
            ))}
          </div>
        )}

        <div className="mp-cuerpo">
          {arriba}

          {aviso && (
            <div className={`mp-aviso mp-aviso--${peligro ? "peligro" : "calma"}`}>
              <Iconos.aviso size={16} strokeWidth={2} /><span>{aviso}</span>
            </div>
          )}
          {consulta && !aviso && (
            <p className="mp-lectura"><Iconos.info size={13} strokeWidth={2} /> Solo lectura: no cambia nada en la ONT</p>
          )}

          {estado === "reposo" && children && <div className="mp-formulario">{children}</div>}

          {consulta && terminado ? (
            <>
              {remate}
              <details className="mp-detalles">
                <summary>Ver lo que se le preguntó a la OLT</summary>
                {listaFases}
              </details>
            </>
          ) : (
            <>
              <span className="mp-rotulo">
                {estado === "reposo" ? (consulta ? "Lo que se va a leer" : "Lo que va a pasar")
                  : terminado ? "Lo que pasó" : "Lo que está pasando"}
              </span>
              {listaFases}
              {/* Siempre que se espera y no hay nada que reproducir: también
                  entre una llamada y la siguiente de un alta. */}
              {estado === "corriendo" && reveladas >= total && (
                <p className="mp-espera">
                  <i />{textoEspera}<span>{segundos.toFixed(1)} s</span>
                </p>
              )}
              {remate}
            </>
          )}
        </div>

        <div className="mp-pie">
          {conGesto && estado === "reposo" && (
            <span className="mp-gesto">{gesto ? "Suelta para cancelar" : "Mantén pulsado para confirmar"}</span>
          )}
          {terminado && segundos > 0 && <span className="mp-duracion">{segundos.toFixed(1)} s</span>}
          <span className="mp-empuja" />
          {consulta && terminado && onEjecutar && (
            <button type="button" className="mp-btn mp-btn--fantasma" onClick={onEjecutar}>
              <Iconos.actualizar size={14} strokeWidth={2} /> Volver a consultar
            </button>
          )}
          <button type="button" className="mp-btn mp-btn--fantasma" onClick={onCerrar} disabled={estado === "corriendo"}>
            {estado === "fin" || consulta ? "Cerrar" : "Cancelar"}
          </button>
          {!consulta && estado !== "fin" && (
            <button
              type="button"
              className={`mp-btn ${peligro ? "mp-btn--peligro" : "mp-btn--principal"}`}
              disabled={!listo || estado !== "reposo"}
              {...manejadores}
            >
              {conGesto && <i className="mp-relleno" ref={relleno} />}
              <span>
                {estado === "corriendo"
                  ? <><Iconos.cargando size={14} strokeWidth={2.4} className="mp-gira" /> Mandando…</>
                  : <>{Icono && <Icono size={15} strokeWidth={2} />} {verbo}</>}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
